import {
  EntityManager,
  EntityRepositoryOptions,
  ShowAllEntityFilter,
  createRepositoryByEntityNameFactory
} from '@record-it-npm/synchro-client';
import { DisposableContainer } from 'record-it-utils/utils/DisposableContainer/DisposableContainer';
import { DebugInterface } from '../debugging/DebugInterface';
import { AppEntityManagerApiAdapter } from './AppEntityManagerApiAdapter';
import { constructionSiteEntityInfo } from './ConstructionSite/constructionSiteEntityInfo';
import { ConstructionSiteRepository } from './ConstructionSite/ConstructionSiteRepository';
import { constructionSiteUsageStatisticEntityInfo } from './ConstructionSiteUsageStatistic/constructionSiteUsageStatisticEntityInfo';
import { ConstructionSiteUsageStatisticRepository } from './ConstructionSiteUsageStatistic/ConstructionSiteUsageStatisticRepository';
import { AppEntityManagerEntityTypesByEntityName } from './AppEntityManagerEntityTypesByEntityName';
import { timeTrackingActivityEntityInfo } from './TimeTrackingActivity/timeTrackingActivityEntityInfo';
import { TimeTrackingActivityRepository } from './TimeTrackingActivity/TimeTrackingActivityRepository';
import { timeTrackingActivityCompilationEntityInfo } from './TimeTrackingActivityCompilation/timeTrackingActivityCompilationEntityInfo';
import { TimeTrackingActivityCompilationRepository } from './TimeTrackingActivityCompilation/TimeTrackingActivityCompilationRepository';
import { timeTrackingConfigurationEntityInfo } from './TimeTrackingConfiguration/timeTrackingConfigurationEntityInfo';
import { TimeTrackingConfigurationRepository } from './TimeTrackingConfiguration/TimeTrackingConfigurationRepository';
import { userEntityInfo } from './User/userEntityInfo';
import { UserRepository } from './User/UserRepository';
import { userGroupEntityInfo } from './UserGroup/userGroupEntityInfo';
import { UserGroupRepository } from './UserGroup/UserGroupRepository';
import { userGroupToUserEntityInfo } from './UserGroupToUser/userGroupToUserEntityInfo';
import { UserGroupToUserRepository } from './UserGroupToUser/UserGroupToUserRepository';
import { workTimeEntryEntityInfo } from './WorkTimeEntry/workTimeEntryEntityInfo';
import { WorkTimeEntryRepository } from './WorkTimeEntry/WorkTimeEntryRepository';
import { V1UuidAdapter } from '../../../../record-it-npm-synchro-common/src/EntityId';
import { AppSynchronizationEnvironmentTypes } from '../../AppSynchronizationEnvironmentTypes';

export class AppEntityManager extends EntityManager<
  AppSynchronizationEnvironmentTypes,
  AppEntityManagerEntityTypesByEntityName,
  EntityRepositoryByEntityName
> {
  private readonly disposableContainer = new DisposableContainer();

  /**
   *
   * @param apiAdapter
   */
  constructor(apiAdapter: AppEntityManagerApiAdapter) {
    super({
      getActualizationsFunction: apiAdapter.getActualizations.bind(apiAdapter),
      uploadFunction: apiAdapter.uploadFunction.bind(apiAdapter),
      createRepositoriesByEntityName,
      createEntityFilter: () => new ShowAllEntityFilter(),
      entityIdAdapter: new V1UuidAdapter()
    });

    DebugInterface.entityManager = this;
  }

  public enableSynchronization(): void {
    this.entitySynchronization.lock(this);
    this.entitySynchronization.start();

    this.disposableContainer.add(
      this.entityActualization.registerHooks({
        afterActualization: () => {
          this.entitySynchronization.unlock(this);
        }
      })
    );

    void this.entityActualization.actualize();
  }

  public disableSynchronization(): void {
    this.entitySynchronization.stop();
  }

  public destroy(): void {
    this.disposableContainer.disposeAll();
    super.destroy();
  }
}

const createRepositoriesByEntityName = createRepositoryByEntityNameFactory<
  AppSynchronizationEnvironmentTypes,
  AppEntityManagerEntityTypesByEntityName
>()(({ entityStorage, idGenerator, filterContainer }) => {
  const options: EntityRepositoryOptions<
    AppSynchronizationEnvironmentTypes,
    AppEntityManagerEntityTypesByEntityName
  > = {
    entityStorage,
    idGenerator,
    filterContainer,
    preserveEntityInstances: false
  };

  return {
    ConstructionSite: new ConstructionSiteRepository(
      constructionSiteEntityInfo,
      options
    ),
    UserGroupToUser: new UserGroupToUserRepository(
      userGroupToUserEntityInfo,
      options
    ),
    ConstructionSiteUsageStatistic:
      new ConstructionSiteUsageStatisticRepository(
        constructionSiteUsageStatisticEntityInfo,
        options
      ),
    TimeTrackingActivityCompilation:
      new TimeTrackingActivityCompilationRepository(
        timeTrackingActivityCompilationEntityInfo,
        options
      ),
    TimeTrackingConfiguration: new TimeTrackingConfigurationRepository(
      timeTrackingConfigurationEntityInfo,
      options
    ),
    TimeTrackingActivity: new TimeTrackingActivityRepository(
      timeTrackingActivityEntityInfo,
      options
    ),
    WorkTimeEntry: new WorkTimeEntryRepository(
      workTimeEntryEntityInfo,
      options
    ),
    User: new UserRepository(userEntityInfo, options),
    UserGroup: new UserGroupRepository(userGroupEntityInfo, options)
  };
});

export type EntityRepositoryByEntityName = ReturnType<
  typeof createRepositoriesByEntityName
>;
